import { useCallback, useMemo, useState } from "react";
import { useToast } from "../../../Hooks/ToasContext";
import { ListCustomersUseCase } from "../../../Usecase/Customers/list-customers-usecase";
import { ICustomerEntity } from "../../../Services/api/customers/customers.interface";
import { DestroyCustomersUseCase } from "../../../Usecase/Customers/destroy-customers-usecase";
import { maskPhone } from "../../../utils/maskPhone";
import { IListSearchPayload } from "../../../Components/List/list.types";

interface IUseListCustomersProps {
  listCustomersUseCase: ListCustomersUseCase
  destroyCustomersUseCase: DestroyCustomersUseCase
}

export function useListCustomers({ listCustomersUseCase, destroyCustomersUseCase }: IUseListCustomersProps){
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [page, setPage] = useState(1)
    const [data, setData] = useState<ICustomerEntity[]>([])
    const [boxToDestroy, setCustomerToDestroy] = useState('')
    const [totalRegisters, setTotalRegisters] = useState(0)
    const [currentSearch, setCurrentSearch] = useState('')
    const { showToast } =  useToast()

    const loadCustomers = useCallback(async ({ page: currentPage, searchBy = '' }: { page: number; searchBy?: string }) => {
      try {
        setLoading(true)
        setLoadError(false)
        
        const listResult = await listCustomersUseCase.handle({ page: currentPage, searchBy });

        if (listResult.isFailure) {
          setLoadError(true)
          showToast(String(listResult?.error?.error), 'error');
          return;
        }

       const list = listResult.getValue()

       setData(list?.data || [])
       setTotalRegisters(list?.total || 0)
       setLoadError(false)
       setCurrentSearch(searchBy)
       setPage(currentPage)
      } catch (error) {
        setLoadError(true)
        console.error("error listCustomers function: ", error)
      } finally {
        setLoading(false);
      }
  }, [listCustomersUseCase, showToast]);

    const handleSearch = useCallback(({ page, searchBy }: IListSearchPayload) => {
      loadCustomers({ page, searchBy: searchBy ?? '' })
    }, [loadCustomers]);

    const handlePageChange = useCallback(({ page, searchBy }: IListSearchPayload) => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      loadCustomers({ page, searchBy: searchBy ?? currentSearch })
    }, [loadCustomers, currentSearch]);

    const list = useMemo(() => {
        return data?.map(customer => ({
            id: customer.id,
            name: customer.name,
            document: customer.document,
            email: customer.email,
            stateRegistration: customer.stateRegistration,
            phone: customer?.phone ? maskPhone(customer?.phone) : '',
        })) || []
    }, [data])

    const handleDestroyCustomer = async () => {
      try {
        setLoading(true)
        
        const destroyResult = await destroyCustomersUseCase.handle(boxToDestroy);

        if (destroyResult.isFailure) {
          showToast(String(destroyResult?.error?.error), 'error');
          return;
        }

        setCustomerToDestroy('')
        showToast('Removido com sucesso.', 'success');
        await loadCustomers({ page: 1, searchBy: currentSearch })
      } catch (error) {
        console.error("error destroyCustomers function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const customerToDestroyName = useMemo(() => {
      if (!boxToDestroy) return ''
      return data.find((customer) => customer.id === boxToDestroy)?.name || 'este cliente'
    }, [boxToDestroy, data])

    return {
        loading,
        loadError,
        loadCustomers,
        list,
        data,
        setCustomerToDestroy,
        boxToDestroy,
        customerToDestroyName,
        handleDestroyCustomer,
        totalRegisters,
        handleSearch,
        handlePageChange,
        currentSearch,
        page,
    }
}
